/**
 * StripePortalLink — single-button entry into the Stripe Customer
 * Portal for the active billing context. The Portal handles every
 * post-signup billing operation (update card, change subscription,
 * download invoices, cancel) so we don't have to reimplement any of
 * it.
 *
 * Backed by:
 *   POST /v1/billing/portal-session  (returns { url })
 *   The saga at redux/sagas/billing/portal.js auto-redirects to that
 *   URL once the response lands; this component just dispatches the
 *   request and renders a loading state while it's in flight.
 */
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, CircularProgress, Tooltip } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { openPortal } from '../../redux/actions/billing/portal';

const StripePortalLink = ({
  variant = 'outlined',
  size = 'small',
  fullWidth = false,
  label = 'Manage billing',
  showIcon = true,
  disabled: disabledProp = false,
  tooltip,
}) => {
  const dispatch = useDispatch();
  const portalState = useSelector((s) => s.billing_portal);
  const subscription = useSelector((s) => s.billing_subscription?.response);
  const isLoading = !!portalState?.isLoading;
  // Stripe must be configured at the API tier for the portal to work;
  // the FE can detect this via /billing/subscription returning
  // stripe_configured=false.
  const notConfigured = subscription && subscription.stripe_configured === false;
  const disabled = disabledProp || isLoading || notConfigured;

  const onClick = () => {
    if (disabled) return;
    dispatch(openPortal());
  };

  const button = (
    <Button
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      disabled={disabled}
      onClick={onClick}
      startIcon={isLoading
        ? <CircularProgress size={14} />
        : (showIcon ? <OpenInNewIcon fontSize="small" /> : null)}
    >
      {label}
    </Button>
  );

  // Wrap in a Tooltip when caller provides one, OR when Stripe isn't
  // configured so the user knows why it's disabled.
  if (tooltip || notConfigured) {
    return (
      <Tooltip title={tooltip || 'Payments are not configured in this environment.'}>
        <span>{button}</span>
      </Tooltip>
    );
  }
  return button;
};

export default StripePortalLink;
